import React from 'react';
import { Settings as SettingsIcon, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { FaconDeTravaillerSettings } from './FaconDeTravaillerSettings';
import { ParametresDelais } from './ParametresDelais';
import { DevisEmailSettingsModal } from './DevisEmailSettingsModal';
import { SignatureSettings } from '../Settings/SignatureSettings';

/**
 * Dossiers › Réglages : l'unique endroit pour régler le devis et l'accord du client (rangement des réglages, 2026-10-28).
 * Reprend tels quels les écrans de « Façon de travailler » (sans le contrat), « Délais », « E-mails du devis » et « Signature en ligne » :
 * mêmes données, mêmes routes ; seul l'emplacement change. Le contrat se règle dans Contrats annuels › Réglages.
 */
export type OngletReglagesDossiers = 'facon' | 'delais' | 'emails' | 'signature';

export function ReglagesDossiers({ onClose, onglet: ongletInitial = 'facon' }: { onClose: () => void; onglet?: OngletReglagesDossiers }) {
    const { user } = useAuth();
    const [onglet, setOnglet] = React.useState<OngletReglagesDossiers>(ongletInitial);
    // Signature en ligne (CGV, rétractation) : réservée au titulaire du compte, comme avant dans Paramètres.
    const onglets: [OngletReglagesDossiers, string][] = [['facon', 'Façon de travailler'], ['delais', 'Délais'], ['emails', 'E-mails du devis'],
        ...(user?.type === 'client' ? [['signature', 'Signature en ligne'] as [OngletReglagesDossiers, string]] : [])];
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="reglages-dossiers-titre">
            <div className="bg-surface border border-border rounded-2xl w-full max-w-3xl max-h-[88vh] shadow-2xl overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
                    <h3 id="reglages-dossiers-titre" className="text-lg font-bold text-content flex items-center gap-2"><SettingsIcon size={18} className="text-brand-500" /> Réglages des dossiers</h3>
                    <button type="button" onClick={onClose} aria-label="Fermer" className="text-content-muted hover:text-content"><X size={20} /></button>
                </div>
                <div className="px-6 pt-3 flex gap-2 shrink-0 flex-wrap" role="tablist">
                    {onglets.map(([cle, libelle]) => (
                        <button key={cle} type="button" role="tab" aria-selected={onglet === cle} onClick={() => setOnglet(cle)}
                            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold border transition-colors ${onglet === cle ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>{libelle}</button>
                    ))}
                </div>
                <div className="overflow-y-auto p-6">
                    {onglet === 'facon' && <FaconDeTravaillerSettings partie="devis" />}
                    {onglet === 'delais' && <ParametresDelais />}
                    {onglet === 'emails' && <DevisEmailSettingsModal embarque onClose={onClose} />}
                    {onglet === 'signature' && <SignatureSettings />}
                </div>
            </div>
        </div>
    );
}
